import { useTranslation } from "react-i18next";
import {
    Container,
    Grid,
    Heading,
    HomePageSkeleton,
    Mono,
    SeatUsageCard,
    Stack,
    StatCard,
    StatCardGroup,
    VStack,
} from "@athena/ui";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { DashboardStatsDocument } from "@/graphql/dashboard.generated";
import { useQuery } from "@apollo/client/react";
import { formatDateTime, getTimeOfDay } from "../../../global/formatHelpers";
import { useNavigate } from "react-router-dom";

export function HomePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data, loading } = useQuery(CurrentUserDocument);
    const user = data?.currentUser;
    const { data: statsData, loading: statsLoading } = useQuery(DashboardStatsDocument);
    const stats = statsData?.dashboardStats;

    const fmt = (n: number | undefined) => (statsLoading || n === undefined ? "—" : n.toLocaleString());
    if (loading && !data) return <HomePageSkeleton />;

    return (
        <Container className="p-6">
            <Stack gap={"none"} className="mb-4">
                <Mono size="xs" muted>
                    {formatDateTime(Date.now())}
                </Mono>
                <Heading level={3}>
                    {t("common.greetings.good")} {t(`common.greetings.${getTimeOfDay(Date.now())}`)},{" "}
                    {user?.firstName || user?.email}
                </Heading>
            </Stack>
            <StatCardGroup className="w-full">
                <StatCard label={t("nav.organizations")} value={fmt(stats?.totalOrganizations)} />
                <StatCard label={t("common.fields.subOrgs")} value={fmt(stats?.totalSubOrganizations)} />
                <StatCard label={t("common.fields.users")} value={fmt(stats?.totalUsers)} />
            </StatCardGroup>
            <StatCardGroup className="mt-4 w-full">
                <StatCard
                    label="Courses"
                    value={fmt(stats?.courses.total)}
                    detail={
                        stats
                            ? `${stats.courses.published.toLocaleString()} published · ${stats.courses.draft.toLocaleString()} draft`
                            : undefined
                    }
                />
                <StatCard
                    label="Assignments"
                    value={fmt(stats?.assignments.total)}
                    detail={
                        stats
                            ? `${stats.assignments.inProgress.toLocaleString()} in progress · ${stats.assignments.completed.toLocaleString()} completed`
                            : undefined
                    }
                />
                <StatCard
                    label="Crisis Alerts (30d)"
                    value={fmt(stats?.crisisAlerts.last30Days)}
                    detail={stats ? `${stats.crisisAlerts.total.toLocaleString()} all-time` : undefined}
                />
                <StatCard label="Unread Messages" value={fmt(stats?.unreadMessages)} />
            </StatCardGroup>
            <Grid cols={1} gap="md" className="mt-4 w-full">
                <VStack gap="md">
                    <SeatUsageCard
                        className="w-full"
                        onAllOrgsClick={() => navigate("/organizations")}
                        orgs={(stats?.orgLicenseUsage ?? []).map((org) => ({
                            id: org.organizationId,
                            name: org.organizationName,
                            fallback: org.organizationName.slice(0, 2).toUpperCase(),
                            used: org.used,
                            total: org.totalLicenses,
                        }))}
                    />
                </VStack>
            </Grid>
        </Container>
    );
}
